<template>
  <div
    :class="[
      'dropdown-holder',
      { disabled: isDisabled },
      { 'is-active': isActive && !isDisabled },
      classes,
    ]"
    ref="dropdownRef"
    @keydown.up.prevent.stop="focusPreviousMenuItem()"
    @keydown.down.prevent.stop="focusNextMenuItem()"
  >
    <template v-if="$slots.labelOverride">
      <slot name="labelOverride" />
    </template>
    <label v-else-if="label" :class="['rbz-label']">
      {{ label }}
      <slot name="postLabel" />
    </label>
    <div
      :class="[
        'dropdown-header',
        { open: isOpen },
        { disabled: isDisabled },
        {
          'tags-list':
            !$slots.headerContentOverride && type === 'multi-select' && !!checkedItems.length,
        },
        {
          hoverable:
            (!$slots.headerContentOverride && type !== 'multi-select') || !checkedItems.length,
        },
        errorMessage.class,
      ]"
      ref="dropdownHeaderRef"
      tabindex="0"
      data-qa="dropdown-header"
      @click="toggleDropdown()"
      @mousedown="trackClicked()"
      @keypress.space.prevent
      @keyup.space.stop="toggleDropdown()"
      @keydown.enter.stop="toggleDropdown()"
      v-b-tooltip="headerTooltip ? headerTooltip : ''"
    >
      <template v-if="$slots.headerContentOverride">
        <slot name="headerContentOverride" :checkedItems="checkedItems" :items="items" />
      </template>
      <template v-else>
        <template v-if="type === 'multi-select' && checkedItems.length">
          <div class="tags-wrapper ellipsis scrollbox-shadowed">
            <template v-if="$slots.headerCustomTagOverride">
              <slot
                name="headerCustomTagOverride"
                :checkedItems="checkedItems"
                :tagClickEnabled="tagClickEnabled"
              />
            </template>
            <template v-else>
              <custom-tag
                v-for="item in checkedItems"
                :key="item[0]"
                :value="item[1].toString()"
                :remove-option="true"
                :click-enabled="tagClickEnabled"
                @remove="removeItem(item)"
              />
            </template>
          </div>
          <div
            class="clear-icon-wrapper"
            tabindex="0"
            data-qa="clear-icon"
            @keypress.space.prevent
            @keyup.space.stop="checkedItems = []"
            @keydown.enter.stop="checkedItems = []"
            @click="clearAllButtonClicked($event)"
          >
            <icon-close color="jewel" />
          </div>
        </template>
        <div v-else class="is-fullwidth dropdown-placeholder selected-value">
          <div class="ellipsis">
            {{ checkedItems[0]?.length ? checkedItems[0][1] : placeholderWithDefaults }}
          </div>
          <div
            v-if="canClearSingleSelect && checkedItems?.[0]?.length"
            class="clear-icon-wrapper"
            tabindex="0"
            data-qa="clear-icon"
            @keypress.space.prevent
            @keyup.space.stop="checkedItems = []"
            @keydown.enter.stop="checkedItems = []"
            @click="clearSingleSelectButtonClicked($event)"
          >
            <icon-close color="jewel" />
          </div>
        </div>
      </template>
      <div class="expand-icon-wrapper">
        <icon-chevron :color="chevronColor" :direction="isOpen ? 'up' : 'down'" />
      </div>
    </div>
    <div v-if="isOpen" class="dropdown-list" ref="dropdownListRef">
      <form-search-field
        v-if="filterActive"
        :variant="filterVariant"
        name="search"
        :placeholder="t('dropdown.searchPlaceholder')"
        ref="searchFieldRef"
        @search="isDisabled ? null : emit('search', $event)"
        @click="handleSearchFieldClick($event)"
        data-qa="search-input"
        v-model:inputValue="searchTerm"
        @update:inputValue="emit('update:searchValue', $event)"
      />
      <menu-list-item
        v-if="type === 'multi-select' && allVisible"
        :title="allLabelWithDefaults"
        :selected="allCheckedOverride ? allCheckedOverrideValue : isAllChecked"
        :indeterminate="allCheckedOverride ? allCheckedOverrideIndeterminate : isAllIndeterminate"
        :disabled="isDisabled || allDisabled"
        @item-click="handleClickAll($event)"
        type="multi-select"
      />
      <form-toggle
        v-if="toggleLabel"
        :buttonValue="toggleValue"
        @update:button-value="emit('update:toggleValue', $event)"
        :label="toggleLabel"
        :disabled="toggleDisabled"
        size="small"
        data-qa="dropdown-toggle"
        class="dropdown-toggle"
      />
      <layout-divider-line
        v-if="(type === 'multi-select' && allVisible) || toggleLabel"
        orientation="horizontal"
        class="all-divider"
        color="light-gray"
      />
      <template v-if="virtualScroll">
        <RecycleScroller
          class="scroller"
          :items="dropdownItemsList"
          :item-size="40"
          key-field="id"
          v-slot="{ item }"
        >
          <menu-list-item
            :disabled="isDisabled || item.disabled || item.option.length < 2"
            :title="item.option[1]?.toString() || item.option[0]?.toString()"
            :subtitle="item.option[2]?.toString() || ''"
            @item-click="handleSingleItemClick($event, item)"
            v-show="filterItems(item, searchTerm)"
            :selected="handleChecked(item.option)"
            :virtual-scroll="true"
            :type="type"
          >
            <template v-if="item?.children">
              <menu-list-item
                v-for="childItem in item.children"
                :key="childItem.id"
                :disabled="isDisabled"
                :title="childItem.option[1]?.toString()"
                :subtitle="childItem.option[2]?.toString() || ''"
                @item-click="handleSingleItemClick($event, item)"
                :selected="handleChecked(childItem.option)"
                :virtual-scroll="true"
                type="multi-select"
              />
            </template>
          </menu-list-item>
        </RecycleScroller>
      </template>
      <template v-else>
        <div :class="['list-radius', { 'list-cropped': items.length >= 6 }]">
          <menu-list-item
            v-for="item in dropdownItemsList as DropdownItem[]"
            :key="item.id"
            :disabled="item.disabled || item.option.length < 2"
            :title="item.option[1]?.toString() || item.option[0]?.toString()"
            :subtitle="item.option[2]?.toString() || ''"
            @item-click="handleSingleItemClick($event, item)"
            v-show="filterItems(item, searchTerm)"
            :selected="handleChecked(item.option)"
            :type="type"
          >
            <template v-if="item?.children">
              <menu-list-item
                v-for="childItem in item.children"
                :key="childItem.id"
                :title="childItem.option[1]?.toString()"
                :subtitle="childItem.option[2]?.toString() || ''"
                @item-click="handleSingleItemClick($event, item)"
                :selected="handleChecked(childItem.option)"
                type="multi-select"
              />
            </template>
          </menu-list-item>
        </div>
      </template>
    </div>
    <span v-if="errorMessage.message" class="error-message">
      {{ errorMessage.message }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import CustomTag from './CustomTag.vue';
import FormSearchField from './FormSearchField.vue';
import MenuListItem from './MenuListItem.vue';
import _ from 'lodash';
import { ListItemType } from '../../rbz/components/MenuListItem.vue';
import IconClose from '../../assets/icons/IconClose.vue';
import IconChevron from '../../assets/icons/IconChevron.vue';
import FormToggle from '../../rbz/components/FormToggle.vue';
import LayoutDividerLine from '../../rbz/components/LayoutDividerLine.vue';
import { DropdownItem } from '../../rbz/types';
import { useI18n } from 'vue-i18n';
import Utils from '../../helpers/Utils';

const { t } = useI18n();

export type ListPosition = 'bottom' | 'top';

export interface FormDropdownProps {
  label?: string;
  placeholder?: string;
  classes?: string;
  disabled?: boolean;
  isUpdateInProcess?: boolean;
  ignoreUpdateInProcess?: boolean;
  required?: boolean;
  isInvalid?: boolean;
  errorMessageText?: string;
  type?: ListItemType;
  canClearSingleSelect?: boolean;
  allVisible?: boolean;
  filterForceDisplay?: boolean;
  filterVariant?: 'default' | 'search-trigger';
  items: DropdownItem[] | [string | number, string][];
  value?: string | string[] | number | number[];
  listPosition?: ListPosition;
  allLabel?: string;
  allDisabled?: boolean;
  allCheckedOverride?: boolean;
  allCheckedOverrideValue?: boolean;
  allCheckedOverrideIndeterminate?: boolean;
  toggleValue?: boolean;
  toggleLabel?: string;
  toggleDisabled?: boolean;
  virtualScroll?: boolean;
  headerTooltip?: string;
  tagClickEnabled?: boolean;
  chevronColor?: string;
  skipSort?: boolean;
  exclusiveOptionIds?: string[];
  customContainer?: HTMLElement | null;
}

const props = withDefaults(defineProps<FormDropdownProps>(), {
  placeholder: null,
  errorMessageText: null,
  required: false,
  disabled: false,
  filterForceDisplay: false,
  filterVariant: 'default',
  type: 'menu',
  canClearSingleSelect: false,
  allVisible: true,
  allLabel: null,
  virtualScroll: false,
  tagClickEnabled: true,
  chevronColor: 'stratos',
  skipSort: false,
  exclusiveOptionIds: (): string[] => [],
  customContainer: null,
});

interface FormDropdownEmits {
  (e: 'update:value', value: string[] | string): void;

  (e: 'search', value: string): void;

  (e: 'isActive', value: boolean): void;

  (e: 'isOpen', value: boolean): void;

  (e: 'update:toggleValue', value: boolean): void;

  (e: 'update:searchValue', value: string): void;
}

const emit = defineEmits<FormDropdownEmits>();

const isOpen = ref<boolean>(false);
const dropdownHeaderRef = ref(null);
const dropdownListRef = ref(null);
const searchFieldRef = ref(null);
const searchTerm = ref<string>('');
const dropdownRef = ref<HTMLElement>(null);
const filterItems = (item: DropdownItem, searchTerm: string) => {
  if (item.option[1] && typeof item.option[1] === 'string') {
    const searchTermLowerCase = searchTerm.toString().toLowerCase();
    const menuItem = item.option[1].toLowerCase().includes(searchTermLowerCase);
    const childItem = item?.children?.some((item) => {
      return item.option[1].toLowerCase().includes(searchTermLowerCase) === true;
    });
    return menuItem || childItem;
  }

  return item.option[0];
};

const placeholderWithDefaults = computed(() => {
  if (!_.isNil(props.placeholder)) {
    return props.placeholder;
  } else {
    return t('dropdown.headerPlaceholder');
  }
});

const errorMessageTextWithDefaults = computed(() => {
  if (!_.isNil(props.errorMessageText)) {
    return props.errorMessageText;
  } else {
    return t('dropdown.errorMessageText');
  }
});

const allLabelWithDefaults = computed(() => {
  if (!_.isNil(props.allLabel)) {
    return props.allLabel;
  } else {
    return t('dropdown.allLabel');
  }
});

const isDisabled = computed(() => {
  return props.disabled || (props.isUpdateInProcess && !props.ignoreUpdateInProcess);
});

const getDropdownItems = (): DropdownItem[] => {
  if (props.items[0] instanceof Object && 'option' in props.items[0]) {
    return (props.items as DropdownItem[]).map((item) => {
      item.id = item.option[0] as string;
      return item;
    });
  } else {
    return [...(props.items as [string | number, string][])].map((item) => {
      return {
        id: item[0] as string,
        option: item as string[],
      };
    });
  }
};
const dropdownItems = ref<DropdownItem[]>(getDropdownItems());
const dropdownItemsList = computed(() => {
  let items = _.filter(dropdownItems.value, (item) => {
    return filterItems(item, searchTerm.value);
  });
  if (!props.skipSort) {
    items = (items as DropdownItem[]).sort((a: DropdownItem, b: DropdownItem) => {
      let sortValueA = a.option[1]?.toString() || a.option[0]?.toString();
      let sortValueB = b.option[1]?.toString() || b.option[0]?.toString();
      const sortValueALowerCase = sortValueA.toLowerCase();
      const sortValueBLowerCase = sortValueB.toLowerCase();
      // only ignore case if the values are different from one another
      if (!_.isEqual(sortValueALowerCase, sortValueBLowerCase)) {
        sortValueA = sortValueALowerCase;
        sortValueB = sortValueBLowerCase;
      }

      // check if items are exclusive, put them to the beginning
      const exclusiveA = props.exclusiveOptionIds.includes(a.id);
      const exclusiveB = props.exclusiveOptionIds.includes(b.id);
      if (exclusiveA && !exclusiveB) return -1;
      if (!exclusiveA && exclusiveB) return 1;

      return sortValueA.localeCompare(sortValueB);
    });
  }
  return items;
});

const getDefaultSelected = (): string[][] => {
  const allItems: string[][] = [];

  if (typeof dropdownItems.value[0]?.option[0] === 'number') {
    return _.map(dropdownItems.value, (item: DropdownItem) => {
      return [item.option[0], item.option[1]];
    }).filter((option: DropdownItem['option']) => {
      if (typeof props.value === 'number') {
        return Number(option[0]) === props.value;
      } else {
        return (props.value as number[])?.includes(Number(option[0]));
      }
    });
  }

  dropdownItems.value.forEach((item: DropdownItem) => {
    allItems.push(item.option);
    if (item?.children) {
      item.children.forEach((childItem: DropdownItem) => {
        allItems.push(childItem.option);
      });
    }
  });

  if (typeof props.value === 'string') {
    return allItems.filter((filteredItem) => {
      return filteredItem[0] === props.value;
    });
  } else {
    return allItems.filter((filteredItem) => {
      return (props.value as string[])?.includes(filteredItem[0]);
    });
  }
};
const checkedItems = ref<string[][]>(null);

watch(
  () => props.items,
  function () {
    updateDropDownItems(true);
  },
  { deep: true }
);

function updateDropDownItems(checkMissingValues: boolean) {
  const newItems = getDropdownItems();
  if (!_.isEqual(dropdownItems.value, newItems)) {
    dropdownItems.value = newItems;
    checkedItems.value = getDefaultSelected();
    if (checkMissingValues) {
      addCheckedMissingValues();
    }
  }
  if (props.exclusiveOptionIds.length > 0) {
    handleExclusiveOptions();
  }
}

const addCheckedMissingValues = () => {
  checkedItems.value = getDefaultSelected();
  if (!props.value) {
    return;
  }
  if (Array.isArray(props.value)) {
    const missingValues = _.differenceWith(
      props.value as (string | number)[],
      dropdownItems.value,
      (selectedValue, dropdownValue) => {
        return dropdownValue.option[0] === selectedValue;
      }
    );
    if (missingValues.length) {
      _.forEach(missingValues, (value) => {
        const valueStr = String(value);
        checkedItems.value.push([valueStr, valueStr]);
        dropdownItems.value.push({
          id: valueStr,
          option: [valueStr, valueStr],
          checked: true,
        });
      });
    }
  } else if (typeof props.value === 'string') {
    const selectedValueNotInItems = !_.some(dropdownItems.value, (dropdownValue) => {
      return dropdownValue.option[0] === props.value;
    });
    if (selectedValueNotInItems) {
      const value = props.value;
      checkedItems.value.push([value, value]);
      dropdownItems.value.push({
        id: value,
        option: [value, value],
        checked: true,
      });
    }
  }
};

watch(
  () => props.value,
  function () {
    addCheckedMissingValues();
  },
  { deep: true, immediate: true }
);
const isDirty = ref(false);
watch(
  () => props.value,
  async () => {
    isDirty.value = true;
  },
  { deep: true }
);

const removeItem = (item: string[], skipExclusiveHandling = false) => {
  checkedItems.value = [...checkedItems.value].filter((checkedItem) => {
    return !_.isEqual(checkedItem, item);
  });
  if (!skipExclusiveHandling && props.exclusiveOptionIds.length > 0) {
    handleExclusiveOptions();
  }
};

const toggleDropdown = () => {
  setDropdownIsOpen(!isOpen.value);
};

const focusDropdown = () => {
  dropdownHeaderRef.value.focus();
};

const filterActive = computed(() => {
  return props.filterForceDisplay || dropdownItems.value.length >= 6;
});

const setDropdownIsOpen = (newIsOpen: boolean) => {
  if (
    event?.elementsClicked?.some((elementName: string) => {
      return [
        'CustomTag',
        'rbzDropdownClearAllButton',
        'rbzDropdownClearSingleSelectButton',
      ].includes(elementName);
    })
  ) {
    return;
  }
  if (searchTerm.value) {
    searchTerm.value = '';
  }
  if (newIsOpen) {
    openMenu();
  } else {
    closeMenu();
  }
  nextTick(() => {
    if (filterActive.value && searchFieldRef.value) {
      searchFieldRef.value.focus();
    } else {
      if (newIsOpen) {
        focusDropdown();
      }
    }
  });
};

const onClick = (itemOption: string[][], checked: boolean) => {
  if (props.type === 'multi-select') {
    return handleMultiSelectClick(itemOption, checked);
  }
  handleSingleSelectClick(itemOption);
};

const handleChecked = (itemOption: string[]) => {
  return checkedItems.value.some((checkedItem) => _.isEqual(checkedItem, itemOption));
};

const handleSingleSelectClick = (item: string[][]) => {
  if (searchTerm.value) {
    searchTerm.value = '';
  }
  checkedItems.value = item;
  closeMenu();
};

const handleMultiSelectClick = (items: string[][], checked: boolean) => {
  items.forEach((item) => {
    if (!handleChecked(item) && checked) {
      checkedItems.value.push(item);
    } else if (handleChecked(item) && !checked) {
      checkedItems.value = _.filter(checkedItems.value, (checkedItem) => {
        return !_.isEqual(checkedItem, item);
      });
    }
  });
};

const handleSearchFieldClick = (event: Event) => {
  if (event) {
    if (!event.elementsClicked) {
      event.elementsClicked = [];
    }
    event.elementsClicked.push('dropdownSearchField');
  }
};

const isClicked = ref(false);
const isActive = computed(() => {
  return isOpen.value || isClicked.value;
});
watch(isActive, (value) => {
  emit('isActive', value);
});
watch(isOpen, (value) => {
  emit('isOpen', value);
});

// Used for enforcing CSS :active
const trackClicked = () => {
  isClicked.value = true;
  const interval = setInterval(() => {
    if (!dropdownHeaderRef.value.matches(':active')) {
      isClicked.value = false;
      clearInterval(interval);
    }
  }, 10);
};

const errorMessage = computed(() => {
  if (props.isInvalid && isDirty.value) {
    return {
      class: 'error',
      message: errorMessageTextWithDefaults,
    };
  }
  return {
    class: '',
    message: '',
  };
});

const isAllChecked = computed(() => {
  const checkedItemsHasExclusiveOption = checkedItems.value.some((checkedItem) => {
    return props.exclusiveOptionIds.includes(checkedItem[0]);
  });

  // All checked
  if (
    !searchTerm.value &&
    checkedItems.value.length === getAllItemsLength() &&
    !checkedItemsHasExclusiveOption
  ) {
    return true;
  }
  // All filtered checked
  const filteredItems = [...(dropdownItems.value as DropdownItem[])].filter((filteredItem) => {
    return (
      filterItems(filteredItem, searchTerm.value) &&
      !props.exclusiveOptionIds.includes(filteredItem.id)
    );
  });
  const isAnyFilteredItemsUnchecked = filteredItems.some((filteredItem) => {
    return !checkedItems.value.some((checkedItem) => {
      return checkedItem[0] === filteredItem.option[0];
    });
  });
  if (searchTerm.value && !isAnyFilteredItemsUnchecked) {
    return true;
  }
  // Else
  return false;
});

const isAllIndeterminate = computed(() => {
  const filteredItems = [...(dropdownItems.value as DropdownItem[])].filter((filteredItem) => {
    return filterItems(filteredItem, searchTerm.value);
  });
  const isAnyFilteredItemsChecked = filteredItems.some((filteredItem) => {
    return checkedItems.value.some((checkedItem) => {
      return (
        checkedItem[0] === filteredItem.option[0] &&
        !props.exclusiveOptionIds.includes(filteredItem.id)
      );
    });
  });
  return !isAllChecked.value && isAnyFilteredItemsChecked;
});

const handleExclusiveOptions = () => {
  const disableItems: string[][] = [];

  const items = [...(dropdownItems.value as DropdownItem[])];
  const hasCheckedExclusiveItem = items.some((item) => {
    return checkedItems.value.some((checkedItem) => {
      return checkedItem[0] === item.option[0] && props.exclusiveOptionIds.includes(item.id);
    });
  });

  items.forEach((item) => {
    if (checkedItems.value.length > 0) {
      if (hasCheckedExclusiveItem) {
        if (handleChecked(item.option)) {
          item.disabled = false;
        } else {
          item.disabled = true;
          disableItems.push(item.option);
        }
      } else {
        if (props.exclusiveOptionIds.includes(item.id)) {
          item.disabled = true;
          disableItems.push(item.option);
        } else {
          item.disabled = false;
        }
      }
    } else {
      item.disabled = false;
    }
  });

  onClick(disableItems, false);
};

const handleClickAll = (checked: boolean) => {
  const allItems: string[][] = [];
  const exclusiveItems: string[][] = [];

  const filteredItems = [...(dropdownItems.value as DropdownItem[])].filter((filteredItem) => {
    return filterItems(filteredItem, searchTerm.value);
  });

  filteredItems.forEach((item) => {
    if (!props.exclusiveOptionIds.includes(item.id)) {
      allItems.push(item.option);
    } else {
      exclusiveItems.push(item.option);
    }

    if (item.children) {
      item.children.forEach((childItem) => {
        allItems.push(childItem.option);
      });
    }
  });
  onClick(allItems, checked);
  onClick(exclusiveItems, false);

  if (props.exclusiveOptionIds.length > 0) {
    handleExclusiveOptions();
  }
};
const handleSingleItemClick = (checked: boolean, item: DropdownItem) => {
  if (['single-select', 'menu'].includes(props.type)) {
    handleSingleSelectClick([item.option]);
    return;
  }
  if (checked) {
    const checkedItemWithChildren = [item.option];
    if (item?.children) {
      item.children.forEach((childItem) => {
        checkedItemWithChildren.push(childItem.option);
      });
    }
    onClick(checkedItemWithChildren, checked);
  } else {
    removeItem(item.option, true);
    if (item?.children) {
      item.children.forEach((childItem) => {
        removeItem(childItem.option, true);
      });
    }
  }
  toggleCheckOnItemAndChildren(checked, item);

  if (props.exclusiveOptionIds.length > 0) {
    handleExclusiveOptions();
  }
};
const toggleCheckOnItemAndChildren = (checked: boolean, item: DropdownItem) => {
  item.checked = checked;
  if (item.children) {
    item.children.forEach((child) => {
      toggleCheckOnItemAndChildren(checked, child);
    });
  }
};

let container: Document | HTMLElement | null = null;

const openMenu = () => {
  isOpen.value = true;
  setTimeout(() => {
    Utils.scrollIntoViewIfNotVisible(dropdownListRef.value);
  }, 0);
  // set timeout to avoid closing the dropdown in the same event that opened it
  setTimeout(() => {
    container = props.customContainer ?? document;
    container.addEventListener('click', closeMenuListener);
    container.addEventListener('keydown', closeMenuListener, true);
  }, 0);
};

const detachCloseMenuListeners = () => {
  if (!container) return;

  container.removeEventListener('click', closeMenuListener);
  container.removeEventListener('keydown', closeMenuListener, true);
  container = null;
};

const closeMenu = () => {
  isOpen.value = false;
  detachCloseMenuListeners();
};

const closeMenuListener = (event: Event) => {
  const isMouseClick = (event as PointerEvent).pointerType === 'mouse';
  const isEscape = (event as KeyboardEvent).code === 'Escape';
  const eventInMenu =
    dropdownRef.value?.contains(event.target as Node) ||
    event.elementsClicked?.includes('menuListItem') ||
    event.elementsClicked?.includes('dropdownSearchField');
  if ((isMouseClick && !eventInMenu) || isEscape) {
    closeMenu();
  }
  if (isEscape) {
    event.stopPropagation();
  }
};
const getAllItemsLength = () => {
  let length = 0;

  dropdownItems.value.forEach((item) => {
    if (item && !props.exclusiveOptionIds.includes(item.id)) {
      length++;
    }
    if (item?.children) {
      item.children.forEach((childItem) => {
        if (childItem) {
          length++;
        }
      });
    }
  });
  return length;
};

const focusPreviousMenuItem = () => {
  let indexToFocus;
  const focusableElements = dropdownRef.value.querySelectorAll("[tabindex='0']");
  _.forEach(focusableElements, (element, index) => {
    if (element.contains(document.activeElement)) {
      indexToFocus = index - 1;
    }
  });
  if (!_.isNil(indexToFocus) && indexToFocus >= 0) {
    (focusableElements[indexToFocus] as HTMLElement).focus();
  }
};
const clearAllButtonClicked = (event: Event) => {
  if (!event.elementsClicked) {
    event.elementsClicked = [];
  }
  event.elementsClicked.push('rbzDropdownClearAllButton');
  checkedItems.value = [];
  if (props.exclusiveOptionIds.length > 0) {
    handleExclusiveOptions();
  }
};
const clearSingleSelectButtonClicked = (event: Event) => {
  if (!event.elementsClicked) {
    event.elementsClicked = [];
  }
  event.elementsClicked.push('rbzDropdownClearSingleSelectButton');
  checkedItems.value = [];
};

const focusNextMenuItem = () => {
  let indexToFocus;
  const focusableElements = dropdownRef.value.querySelectorAll("[tabindex='0']");
  _.forEach(focusableElements, (element, index) => {
    if (element.contains(document.activeElement)) {
      indexToFocus = index + 1;
    }
  });
  if (_.isNil(indexToFocus)) {
    indexToFocus = 0;
  }
  if (!_.isNil(indexToFocus) && indexToFocus < focusableElements.length) {
    (focusableElements[indexToFocus] as HTMLElement).focus();
  }
};

watch(
  checkedItems,
  (value) => {
    if (!value || _.isEqual(value, getDefaultSelected())) {
      return;
    }
    if (props.type === 'multi-select') {
      emit(
        'update:value',
        [...checkedItems.value].map((checkedItem) => checkedItem[0])
      );
      return;
    }
    emit('update:value', checkedItems.value[0] ? checkedItems.value[0][0] : '');
  },
  { deep: true }
);

onBeforeUnmount(() => {
  detachCloseMenuListeners();
});

defineExpose({
  toggleDropdown,
  focusDropdown,
  dropdownListRef,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.dropdown-holder {
  align-self: stretch;
  box-sizing: border-box;
  flex: none;
  flex-grow: 0;
  order: 0;
  position: relative;
}

.dropdown-holder > * {
  box-sizing: border-box;
}

.dropdown-header {
  align-items: center;
  background-color: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'silver');
  border-radius: 4px;
  box-sizing: border-box;
  color: map.get(colors.$colors, 'stratos');
  cursor: pointer;
  display: flex;
  gap: 4px;
  justify-content: space-between;
  max-height: 96px;
  min-height: 36px;
  overflow-y: hidden;
  padding: 6px 12px;
  position: relative;

  &.hoverable:hover {
    background-color: map.get(colors.$colors, 'white-ice');
  }

  &.hoverable:active {
    background-color: map.get(colors.$colors, 'white-ice');
    border: 1px solid map.get(colors.$colors, 'jewel');
  }

  &:focus {
    outline: none;
  }

  &:focus:not(:active) {
    border: 1px solid map.get(colors.$colors, 'jewel');
    box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
  }

  &.open {
    border-radius: 4px 4px 0 0;
    border: 1px solid map.get(colors.$colors, 'silver');
  }

  .dropdown-placeholder {
    color: map.get(colors.$colors, 'stratos');
    display: flex;
    justify-content: space-between;
    align-items: center;
    overflow: hidden;
  }

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }
}

.dropdown-header-arrow {
  path {
    width: 14px;
  }
}

.dropdown-list {
  background-color: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'silver');
  border-radius: 0 0 4px 4px;
  box-shadow: 1px 1px 4px 0 rgba(0, 0, 0, 0.08);
  position: absolute;
  z-index: 90010;
  width: auto;
  min-width: 100%;
  border-top-width: 1px;
  margin-top: -1px;
}

.list-radius {
  border-radius: 0 0 4px 4px;
}

.list-cropped {
  height: 200px;
  overflow: hidden auto;
}

:deep(.vue-recycle-scroller) {
  max-height: 200px;
}

.error {
  background-color: map.get(colors.$colors, 'white');
  border: 1px solid map.get(colors.$colors, 'cinnabar');
  border-radius: 4px;
  box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
  flex: none;
  flex-grow: 0;
  order: 1;
}

.error-message {
  bottom: -17px;
  color: map.get(colors.$colors, 'flamingo');
  flex: none;
  flex-grow: 0;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  order: 1;
  position: absolute;
}

.tags-list {
  align-items: normal;
  gap: 0;

  &:active {
    background-color: map.get(colors.$colors, 'white');
  }
}

:deep(.tags-wrapper) {
  align-items: center;
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap;
  gap: 8px;
  overflow-y: auto;
  padding: 0 2px;

  .custom-tag {
    pointer-events: all;
  }
}

.clear-icon-wrapper,
.expand-icon-wrapper {
  border-radius: 4px;
  display: flex;
  height: fit-content;
}

.clear-icon-wrapper:focus:not(:active) {
  box-shadow: 0 0 0 2px map.get(colors.$colors, 'magic-mint');
  outline: none;
}

.all-divider {
  margin-left: 10px;
  width: calc(100% - 10px);
}

:deep(.dropdown-toggle) {
  margin-bottom: 12px;
  padding: 0 40px;
}
</style>
